import { useEffect, useState } from 'react'

export interface TurnClockProps {
  /** Absolute deadline from the server. */
  endsAt: number | null
  /** Shown larger on the television than on a phone. */
  size?: 'normal' | 'large'
}

/** How long is left, never negative. */
function secondsUntil(endsAt: number, now: number): number {
  return Math.max(0, Math.ceil((endsAt - now) / 1000))
}

/**
 * Counts down to the server's deadline.
 *
 * The server sends an absolute time rather than a duration, so a phone that
 * was asleep for a minute shows the truth on waking instead of resuming the
 * countdown from where it left off.
 *
 * State is a plain tick rather than the remaining seconds: deriving the number
 * during render keeps it correct after any re-render, and avoids writing
 * derived state from an effect.
 *
 * It only turns urgent near the end. A clock that looked alarming for a minute
 * and a half would hurry a child who is simply thinking.
 */
export function TurnClock({ endsAt, size = 'normal' }: TurnClockProps) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (endsAt === null) return

    const tick = setInterval(() => setNow(Date.now()), 500)
    return () => clearInterval(tick)
  }, [endsAt])

  if (endsAt === null) return null

  const left = secondsUntil(endsAt, now)
  const urgent = left <= 10

  // Announced at ten seconds, then over the last five. Announcing every second
  // for the whole final stretch turns a screen reader into a metronome, which
  // is more pressure than the clock is meant to apply.
  const announce = left === 10 || left <= 5

  return (
    <p
      className={[
        'font-display tabular-nums',
        size === 'large' ? 'text-(length:--text-title)' : 'text-(length:--text-heading)',
        urgent ? 'text-coral' : 'text-ink-muted',
      ].join(' ')}
      // Announced only when it matters, so a screen reader is not reading a
      // number aloud every second for a minute and a half.
      role={announce ? 'status' : undefined}
      aria-label={announce ? `${left} seconds left` : undefined}
    >
      {left}s
    </p>
  )
}
